import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, Tobacco, Venue, CellarTobacco, CellarPipe, CellarLighter } from '../lib/supabase';
import { useEmberStore } from '../lib/store';
import { getPairing } from '../lib/pairings';
import { getWeatherRecommendation } from '../lib/weather';
import { isImageFile } from '../lib/imageCompression';
import BottomNav from '../components/BottomNav';
import { evaluateAndAwardBadges } from '../lib/badgesLogic';
import { toast } from '../lib/toast';
import Button from '../components/ui/Button';
import { ArrowLeft, Star, Image as ImageIcon } from 'lucide-react';
import { loadDraftState, saveDraftState, clearDraftState } from '../lib/draftStateStore';
import { dataUrlToFile } from '../lib/draftPhotoStore';
import { usePhotoStorage, StoredPhoto } from '../hooks/usePhotoStorage';
import { usePhotoProcessor } from '../hooks/usePhotoProcessor';

const CHECKIN_DRAFT_KEY = 'ember.checkinDraft.v1';
interface CheckInDraftStored {
  locationName: string;
  tobaccoInput: string;
  selectedCellarTobaccoId: string | null;
  tobaccoManual: string;
  rating: number;
  notes: string;
  pipeInput: string;
  selectedCellarPipeId: string | null;
  pipeManual: string;
  drinkPairing: string;
  lighterInput: string;
  selectedCellarLighterId: string | null;
  lighterManual: string;
  outdoorSeating: boolean;
  coveredSeating: boolean;
  heaters: boolean;
  isPublic: boolean;
  selectedVenueId: string | null;
  photoPreview: string | null;
  photoName: string | null;
  photoType: string | null;
}

export default function CheckIn() {
  const navigate = useNavigate();
  const { currentUser, addCheckIn } = useEmberStore();
  const draftLoadedRef = useRef(false);
  const { loadPhoto, loadPhotoSync, savePhoto, savePhotoData, clearPhoto } = usePhotoStorage(CHECKIN_DRAFT_KEY);
  const { processPhoto } = usePhotoProcessor(CHECKIN_DRAFT_KEY);
  const initialStoredPhoto = loadPhotoSync();
  const [photoData, setPhotoData] = useState<StoredPhoto | null>(initialStoredPhoto);
  const [photoFile, setPhotoFile] = useState<File | null>(() =>
    initialStoredPhoto ? dataUrlToFile(initialStoredPhoto.data, initialStoredPhoto.name, initialStoredPhoto.type) : null
  );
  const [photoPreview, setPhotoPreview] = useState<string | null>(initialStoredPhoto ? initialStoredPhoto.data : null);
  
  // Global database items
  const [tobaccos, setTobaccos] = useState<Tobacco[]>([]);
  const [venues, setVenues] = useState<Venue[]>([]);
  
  // Cellar items
  const [cellarTobacco, setCellarTobacco] = useState<CellarTobacco[]>([]);
  const [cellarPipes, setCellarPipes] = useState<CellarPipe[]>([]);
  const [cellarLighters, setCellarLighters] = useState<CellarLighter[]>([]);
  
  // Selected items
  const [selectedTobacco, setSelectedTobacco] = useState<Tobacco | null>(null);
  const [selectedCellarTobaccoId, setSelectedCellarTobaccoId] = useState<string | null>(null);
  const [selectedCellarPipeId, setSelectedCellarPipeId] = useState<string | null>(null);
  const [selectedCellarLighterId, setSelectedCellarLighterId] = useState<string | null>(null);
  const [tobaccoManual, setTobaccoManual] = useState('');
  const [pipeManual, setPipeManual] = useState('');
  const [lighterManual, setLighterManual] = useState('');
  
  // Check-in details
  const [rating, setRating] = useState(0);
  const [notes, setNotes] = useState('');
  const [locationName, setLocationName] = useState('');
  const [selectedVenueId, setSelectedVenueId] = useState<string | null>(null);
  const [drinkPairing, setDrinkPairing] = useState('');
  const [outdoorSeating, setOutdoorSeating] = useState(false);
  const [coveredSeating, setCoveredSeating] = useState(false);
  const [heaters, setHeaters] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [coords, setCoords] = useState<{lat:number, lon:number} | null>(null);
  const [compressing, setCompressing] = useState(false);
  const [pairing, setPairing] = useState<any>(null);
  const [creatingLocation, setCreatingLocation] = useState(false);

  // Type-ahead inputs for single-box UX
  const [tobaccoInput, setTobaccoInput] = useState('');
  const [pipeInput, setPipeInput] = useState('');
  const [lighterInput, setLighterInput] = useState('');
  const [showTobaccoMatches, setShowTobaccoMatches] = useState(false);
  const [showPipeMatches, setShowPipeMatches] = useState(false);
  const [showLighterMatches, setShowLighterMatches] = useState(false);

  useEffect(() => {
    let isActive = true;

    (async () => {
      try {
        const stored = await loadDraftState<CheckInDraftStored>(CHECKIN_DRAFT_KEY);
        if (!isActive) {
          return;
        }

        let photoName: string | null = null;
        let photoType: string | null = null;
        if (stored) {
          if (typeof stored.locationName === 'string') setLocationName(stored.locationName);
          if (typeof stored.tobaccoInput === 'string') setTobaccoInput(stored.tobaccoInput);
          if ('selectedCellarTobaccoId' in stored) setSelectedCellarTobaccoId(stored.selectedCellarTobaccoId ?? null);
          if (typeof stored.tobaccoManual === 'string') setTobaccoManual(stored.tobaccoManual);
          if (typeof stored.rating === 'number') setRating(stored.rating);
          if (typeof stored.notes === 'string') setNotes(stored.notes);
          if (typeof stored.pipeInput === 'string') setPipeInput(stored.pipeInput);
          if ('selectedCellarPipeId' in stored) setSelectedCellarPipeId(stored.selectedCellarPipeId ?? null);
          if (typeof stored.pipeManual === 'string') setPipeManual(stored.pipeManual);
          if (typeof stored.drinkPairing === 'string') setDrinkPairing(stored.drinkPairing);
          if (typeof stored.lighterInput === 'string') setLighterInput(stored.lighterInput);
          if ('selectedCellarLighterId' in stored) setSelectedCellarLighterId(stored.selectedCellarLighterId ?? null);
          if (typeof stored.lighterManual === 'string') setLighterManual(stored.lighterManual);
          if (typeof stored.outdoorSeating === 'boolean') setOutdoorSeating(stored.outdoorSeating);
          if (typeof stored.coveredSeating === 'boolean') setCoveredSeating(stored.coveredSeating);
          if (typeof stored.heaters === 'boolean') setHeaters(stored.heaters);
          if (typeof stored.isPublic === 'boolean') setIsPublic(stored.isPublic);
          if ('selectedVenueId' in stored) setSelectedVenueId(stored.selectedVenueId ?? null);
          if ('photoName' in stored) photoName = stored.photoName ?? null;
          if ('photoType' in stored) photoType = stored.photoType ?? null;
        }

        if (stored?.photoPreview) {
          const restoredPhoto: StoredPhoto = {
            name: (photoName && typeof photoName === 'string' && photoName.trim()) ? photoName : 'checkin-photo.jpg',
            type: (photoType && typeof photoType === 'string' && photoType.trim()) ? photoType : 'image/jpeg',
            data: stored.photoPreview,
            processed: true,
            processedAt: new Date().toISOString(),
          };
          void savePhotoData(restoredPhoto);
          setPhotoData(restoredPhoto);
        } else {
          const restored = await loadPhoto();
          if (!isActive) {
            return;
          }
          if (restored) {
            setPhotoData(restored);
          }
        }
      } catch (error) {
        console.warn('Failed to load check-in draft', error);
      } finally {
        if (isActive) {
          draftLoadedRef.current = true;
        }
      }
    })();

    return () => {
      isActive = false;
    };
  }, [savePhotoData, loadPhoto]);

  useEffect(() => {
    if (!draftLoadedRef.current) {
      return;
    }
    const draft: CheckInDraftStored = {
      locationName,
      tobaccoInput,
      selectedCellarTobaccoId,
      tobaccoManual,
      rating,
      notes,
      pipeInput,
      selectedCellarPipeId,
      pipeManual,
      drinkPairing,
      lighterInput,
      selectedCellarLighterId,
      lighterManual,
      outdoorSeating,
      coveredSeating,
      heaters,
      isPublic,
      selectedVenueId,
      photoPreview: photoData?.data ?? null,
      photoName: photoData?.name ?? null,
      photoType: photoData?.type ?? null,
    };
    void saveDraftState(CHECKIN_DRAFT_KEY, draft);
  }, [
    locationName,
    tobaccoInput,
    selectedCellarTobaccoId,
    tobaccoManual,
    rating,
    notes,
    pipeInput,
    selectedCellarPipeId,
    pipeManual,
    drinkPairing,
    lighterInput,
    selectedCellarLighterId,
    lighterManual,
    outdoorSeating,
    coveredSeating,
    heaters,
    isPublic,
    selectedVenueId,
    photoData,
  ]);

  useEffect(() => {
    let cancelled = false;

    if (!photoData) {
      setPhotoPreview(null);
      setPhotoFile(null);
      return;
    }

    setPhotoPreview(photoData.data);
    const restoredFile = dataUrlToFile(
      photoData.data,
      (photoData.name && photoData.name.trim()) ? photoData.name : 'checkin-photo.jpg',
      photoData.type || 'image/jpeg',
    );
    setPhotoFile(restoredFile);

    if (!photoData.processed) {
      setCompressing(true);
      processPhoto()
        .then((processed) => {
          if (!cancelled && processed) {
            setPhotoData(processed);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setCompressing(false);
          }
        });
    }

    return () => {
      cancelled = true;
    };
  }, [photoData, processPhoto]);

  const formatStorageType = (storage?: CellarTobacco['storage_type']) => {
    if (!storage) return '';
    return storage
      .split(/[_\s-]+/)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  };

  const openCellarTobacco = useMemo(() => cellarTobacco.filter((item) => item.status === 'opened'), [cellarTobacco]);
  const normalizedTobaccoInput = useMemo(() => tobaccoInput.toLowerCase().replace(/\s*\(.*?\)\s*$/, '').trim(), [tobaccoInput]);

  useEffect(() => {
    loadTobaccos();
    loadVenues();
    loadCellarItems();
    if (navigator.geolocation) {
      console.log('[GPS] Requesting initial location...');
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          console.log('[GPS] Initial location acquired:', pos.coords.latitude, pos.coords.longitude, 'accuracy:', pos.coords.accuracy + 'm');
          setCoords({ lat: pos.coords.latitude, lon: pos.coords.longitude });
        },
        (err) => {
          console.error('[GPS] Initial location failed:', err.code, err.message);
          setCoords(null);
        },
        { enableHighAccuracy: true, maximumAge: 60000, timeout: 10000 }
      );
    } else {
      console.warn('[GPS] Geolocation not supported by browser');
    }
  }, [currentUser]);

  useEffect(() => {
    if (selectedTobacco) {
      const pairingData = getPairing(selectedTobacco.flavor_tags);
      setPairing(pairingData);
    }
  }, [selectedTobacco]);

  async function loadTobaccos() {
    const { data, error } = await supabase
      .from('tobaccos')
      .select('*')
      .order('name');

    if (data) setTobaccos(data);
  }

  async function loadVenues() {
    const { data, error } = await supabase
      .from('venues')
      .select('*')
      .order('name');

    if (data) setVenues(data);
  }

  async function loadCellarItems() {
    if (!currentUser) return;

    // Load cellar tobacco
    const { data: tobaccoData } = await supabase
      .from('cellar_tobacco')
      .select('*')
      .eq('user_id', currentUser.id)
      .order('created_at', { ascending: false });
    
    if (tobaccoData) setCellarTobacco(tobaccoData);

    // Load cellar pipes
    const { data: pipesData } = await supabase
      .from('cellar_pipes')
      .select('*')
      .eq('user_id', currentUser.id)
      .order('created_at', { ascending: false });
    
    if (pipesData) setCellarPipes(pipesData);

    // Load cellar lighters
    const { data: lightersData } = await supabase
      .from('cellar_lighters')
      .select('*')
      .eq('user_id', currentUser.id)
      .order('created_at', { ascending: false });
    
    if (lightersData) setCellarLighters(lightersData);
  }

  // Keep input text in sync when selecting from cellar
  useEffect(() => {
    if (selectedCellarTobaccoId) {
      const it = cellarTobacco.find(t => t.id === selectedCellarTobaccoId);
      if (it) {
        const storage = formatStorageType(it.storage_type);
        setTobaccoInput(`${it.brand} - ${it.blend_name}${storage ? ` (${storage})` : ''}`);
      }
    }
  }, [selectedCellarTobaccoId, cellarTobacco]);

  useEffect(() => {
    if (selectedCellarPipeId) {
      const it = cellarPipes.find(p => p.id === selectedCellarPipeId);
      if (it) setPipeInput(`${it.maker} ${it.shape}${it.style ? ` (${it.style})` : ''}`);
    }
  }, [selectedCellarPipeId, cellarPipes]);

  useEffect(() => {
    if (selectedCellarLighterId) {
      const it = cellarLighters.find(l => l.id === selectedCellarLighterId);
      if (it) setLighterInput(`${it.brand}${it.style ? ` - ${it.style}` : ''} (${it.type === 'lighter' ? 'Lighter' : 'Matches'})`);
    }
  }, [selectedCellarLighterId, cellarLighters]);

  async function handleUseCurrentLocation() {
    if (!coords) {
      toast.error('Location not available. Please enable location services.');
      return;
    }

    // Simple GPS coordinate approach - perfect for coastal paths, mountain views, etc.
    const lat = coords.lat.toFixed(6);
    const lng = coords.lon.toFixed(6);
    const locationName = `Location (${lat}, ${lng})`;
    
    setLocationName(locationName);
    setSelectedVenueId(null);
  }

  async function handlePhotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) {
      setPhotoData(null);
      setPhotoPreview(null);
      setPhotoFile(null);
      clearPhoto();
      return;
    }

    if (!isImageFile(file)) {
      toast.error('Please select a valid image file');
      return;
    }

    setCompressing(true);
    try {
      const stored = await savePhoto(file);
      if (stored) {
        setPhotoData(stored);
      }
    } catch (error) {
      console.error('Failed to save photo', error);
      toast.error('Failed to process image');
    } finally {
      setCompressing(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    
    // Require either cellar tobacco, global tobacco, or manual entry
    const hasTobacco = selectedCellarTobaccoId || selectedTobacco || tobaccoManual.trim();
    if (!hasTobacco || rating === 0) {
      toast.error('Please select a tobacco and rating');
      return;
    }

    // REQUIRED: Location name
    if (!locationName.trim()) {
      toast.error('Please enter a location name');
      return;
    }

    setLoading(true);
    
    // Add a safety timeout to prevent infinite loading
    const safetyTimeout = setTimeout(() => {
      console.error('Check-in submission timeout - resetting loading state');
      setLoading(false);
      toast.error('Submission timed out. Please try again.');
    }, 15000); // 15 second safety timeout (GPS: 10s + Weather: 3s + buffer)
    try {
      // Capture fresh GPS coordinates when submitting
      let currentCoords = coords;
      if (navigator.geolocation) {
        try {
          console.log('[GPS] Requesting fresh location on submit...');
          const position = await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              maximumAge: 30000, // 30 seconds
              timeout: 10000 // 10 second timeout
            });
          });
          console.log('[GPS] Fresh location acquired:', position.coords.latitude, position.coords.longitude, 'accuracy:', position.coords.accuracy + 'm');
          currentCoords = { lat: position.coords.latitude, lon: position.coords.longitude };
        } catch (geoError: any) {
          console.error('[GPS] Fresh location failed:', geoError.code, geoError.message);
          console.warn('[GPS] Using cached coordinates:', currentCoords);
          // Show toast to inform user
          if (currentCoords) {
            toast.info('Using approximate location (GPS unavailable)');
          }
        }
      }

      // Get weather data with timeout fallback
      console.log('[CHECKIN] Starting weather API call...');
      let weather: {
        temperature: number | null;
        humidity: number | null;
        season: string;
        recommendation: string;
      };
      try {
        weather = await Promise.race([
          getWeatherRecommendation(currentCoords?.lat, currentCoords?.lon),
          new Promise<never>((_, reject) => 
            setTimeout(() => reject(new Error('Weather API timeout')), 3000)
          )
        ]);
        console.log('[CHECKIN] Weather API completed successfully');
      } catch (error) {
        console.warn('[CHECKIN] Weather API failed, using fallback:', error);
        weather = {
          temperature: null,
          humidity: null,
          season: "Unknown",
          recommendation: "Choose any blend you enjoy!",
        };
      }

      // Upload photo if present
      console.log('[CHECKIN] Starting photo upload...');
      let photo_url: string | null = null;
      if (photoFile) {
        const filePath = `${currentUser?.id}/${Date.now()}-${photoFile.name}`;
        const { data: up, error: upErr } = await supabase.storage
          .from('checkin-photos')
          .upload(filePath, photoFile, { upsert: false });
        if (!upErr && up) {
          const { data: pub } = supabase.storage
            .from('checkin-photos')
            .getPublicUrl(up.path);
          photo_url = pub?.publicUrl ?? null;
        }
        console.log('[CHECKIN] Photo upload completed');
      } else {
        console.log('[CHECKIN] No photo to upload');
      }

      console.log('[CHECKIN] Submitting check-in with:', {
        photo_url,
        outdoor_seating: outdoorSeating,
        covered_seating: coveredSeating,
        heaters,
        venue_id: selectedVenueId
      });

      console.log('[CHECKIN] Starting database insertion...');

      // Ensure a human-readable tobacco name is always stored for all viewers
      let tobaccoManualValue = tobaccoManual.trim() || null;
      if (!tobaccoManualValue) {
        if (selectedCellarTobaccoId) {
          const ct = cellarTobacco.find((t) => t.id === selectedCellarTobaccoId);
          if (ct) tobaccoManualValue = `${ct.brand} - ${ct.blend_name}`;
        } else if (selectedTobacco) {
          tobaccoManualValue = selectedTobacco.brand ? `${selectedTobacco.brand} - ${selectedTobacco.name}` : selectedTobacco.name;
        }
      }
      // Map 10-star UI rating to 1–5 for storage (DB constraint) – display remains 10-star
      const ratingToStore = Math.max(1, Math.min(5, Math.ceil((rating || 1) / 2)));

      const { data, error } = await supabase
        .from('checkins')
        .insert({
          user_id: currentUser?.id,
          // Cellar references
          cellar_tobacco_id: selectedCellarTobaccoId,
          cellar_pipe_id: selectedCellarPipeId,
          cellar_lighter_id: selectedCellarLighterId,
          // Global tobacco (backward compatibility)
          tobacco_id: selectedTobacco?.id || null,
          // Manual / fallback display entries
          tobacco_manual: tobaccoManualValue,
          pipe_manual: pipeManual.trim() || null,
          lighter_manual: lighterManual.trim() || null,
          // Check-in details
          venue_id: selectedVenueId,
          rating: ratingToStore,
          notes,
          pairing_alcoholic: pairing?.alcoholic,
          pairing_non_alcoholic: pairing?.nonAlcoholic,
          drink_pairing: drinkPairing.trim() || null,
          weather: {
            temperature: weather.temperature,
            humidity: weather.humidity,
            season: weather.season,
          },
          outdoor_seating: outdoorSeating,
          covered_seating: coveredSeating,
          heaters: heaters,
          photo_url,
          latitude: currentCoords?.lat || null,
          longitude: currentCoords?.lon || null,
          is_public: isPublic,
          location_name: locationName.trim() || null,
        })
        .select(`
          *,
          tobacco:tobaccos(*),
          venue:venues(*),
          cellar_tobacco:cellar_tobacco(*),
          cellar_pipe:cellar_pipes(*),
          cellar_lighter:cellar_lighters(*)
        `)
        .single();

      if (error) throw error;
      if (data) {
        console.log('[CHECKIN] Database insertion successful, adding to store...');
        addCheckIn(data as any);
        if (currentUser?.id) {
          console.log('[CHECKIN] Evaluating badges...');
          await evaluateAndAwardBadges(currentUser.id);
        }
        console.log('[CHECKIN] Check-in creation completed successfully!');
        toast.success('Check-in created successfully!');
        draftLoadedRef.current = false;
        await Promise.all([
          clearDraftState(CHECKIN_DRAFT_KEY),
        ]);
        clearPhoto();
        setPhotoData(null);
        setPhotoFile(null);
        setPhotoPreview(null);
        navigate('/');
      }
    } catch (err) {
      console.error('Error creating check-in:', err);
      toast.error('Failed to create check-in. Please try again.');
    } finally {
      clearTimeout(safetyTimeout);
      setLoading(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', paddingBottom: '120px' }}>
      <header style={{
        padding: '1rem',
        borderBottom: '1px solid #6b4423',
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', color: '#ff9500', cursor: 'pointer' }}
        >
          <ArrowLeft size={24} />
        </button>
        <h1 style={{ fontSize: '1.5rem' }}>New Check-in</h1>
      </header>

      <div className="container" style={{ paddingTop: '1rem' }}>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          
          {/* Location Selection */}
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Location *
            </label>
            
            <input
              type="text"
              value={locationName}
              onChange={(e) => setLocationName(e.target.value)}
              placeholder="e.g., Starbucks on Main St, My Backyard, etc."
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: 'var(--color-card)',
                border: '1px solid var(--color-border)',
                borderRadius: '6px',
                color: 'white',
                fontSize: '0.875rem',
                fontFamily: 'inherit'
              }}
              required
            />
          </div>

          {/* Tobacco/Cigar - single input with My Cellar suggestions + manual */}
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Tobacco/Cigar *
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                value={tobaccoInput}
                onChange={(e) => {
                  setTobaccoInput(e.target.value);
                  // Switch to manual if user edits text
                  setSelectedCellarTobaccoId(null);
                  setSelectedTobacco(null);
                  setTobaccoManual(e.target.value);
                  setShowTobaccoMatches(true);
                }}
                placeholder="Type to search your cellar or enter manually..."
                style={{ width: '100%' }}
                onFocus={() => setShowTobaccoMatches(true)}
                onBlur={() => setTimeout(() => setShowTobaccoMatches(false), 150)}
                required={!selectedCellarTobaccoId && !selectedTobacco && !tobaccoManual.trim()}
              />
              {showTobaccoMatches && tobaccoInput.trim().length > 0 && (
                <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 1000, backgroundColor: '#1a1a1a', border: '1px solid var(--color-border)', borderRadius: '6px', maxHeight: '220px', overflowY: 'auto' }}>
                  {openCellarTobacco
                    .filter((t) => {
                      if (!normalizedTobaccoInput) return true;
                      const haystack = `${t.brand} ${t.blend_name}`.toLowerCase();
                      return haystack.includes(normalizedTobaccoInput);
                    })
                    .slice(0, 12)
                    .map(t => (
                      <button key={t.id} type="button" onMouseDown={() => {
                        setSelectedCellarTobaccoId(t.id);
                        setTobaccoManual('');
                        const storage = formatStorageType(t.storage_type);
                        setTobaccoInput(`${t.brand} - ${t.blend_name}${storage ? ` (${storage})` : ''}`);
                        setShowTobaccoMatches(false);
                      }} style={{ width: '100%', textAlign: 'left', padding: '0.5rem', background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}>
                        {t.brand} - {t.blend_name}
                        {(() => {
                          const storage = formatStorageType(t.storage_type);
                          return storage ? ` (${storage})` : '';
                        })()}
                        {t.amount ? ` • ${t.amount}${t.unit === 'grams' ? 'g' : 'oz'}` : ''}
                      </button>
                    ))}
                </div>
              )}
            </div>
          </div>

          {/* Rating (1-10) */}
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Rating
            </label>
            <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
              {[1,2,3,4,5,6,7,8,9,10].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                >
                  <Star
                    size={24}
                    fill={star <= rating ? '#ff9500' : 'none'}
                    color={star <= rating ? '#ff9500' : '#6b4423'}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Share your thoughts about this smoke..."
              rows={3}
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: 'var(--color-card)',
                border: '1px solid var(--color-border)',
                borderRadius: '6px',
                color: 'white',
                fontSize: '0.875rem',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
            />
          </div>


          {/* Pipe - single input with My Cellar suggestions + manual */}
          {currentUser?.smoker_type !== 'cigar' && (
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Pipe (Optional)
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={pipeInput}
                  onChange={(e) => {
                    setPipeInput(e.target.value);
                    setSelectedCellarPipeId(null);
                    setPipeManual(e.target.value);
                    setShowPipeMatches(true);
                  }}
                  placeholder="Type to search your cellar or enter manually..."
                  style={{ width: '100%' }}
                  onFocus={() => setShowPipeMatches(true)}
                  onBlur={() => setTimeout(() => setShowPipeMatches(false), 150)}
                />
                {showPipeMatches && pipeInput.trim().length > 0 && (
                  <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 1000, backgroundColor: '#1a1a1a', border: '1px solid var(--color-border)', borderRadius: '6px', maxHeight: '220px', overflowY: 'auto' }}>
                    {cellarPipes
                      .filter(p => `${p.maker} ${p.shape} ${p.style || ''}`.toLowerCase().includes(pipeInput.toLowerCase()))
                      .slice(0, 12)
                      .map(p => (
                        <button key={p.id} type="button" onMouseDown={() => {
                          setSelectedCellarPipeId(p.id);
                          setPipeManual('');
                          setPipeInput(`${p.maker} ${p.shape}${p.style ? ` (${p.style})` : ''}`);
                          setShowPipeMatches(false);
                        }} style={{ width: '100%', textAlign: 'left', padding: '0.5rem', background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}>
                          {p.maker} {p.shape} {p.style && `(${p.style})`}
                        </button>
                      ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Drink Pairing */}
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Paired With (Optional)
            </label>
            <input
              type="text"
              value={drinkPairing}
              onChange={(e) => setDrinkPairing(e.target.value)}
              placeholder="e.g., Coffee, Whiskey, Earl Grey Tea..."
              style={{ width: '100%' }}
            />
          </div>

          {/* Lighter/Matches - single input with My Cellar suggestions + manual */}
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Lighter/Matches (Optional)
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                value={lighterInput}
                onChange={(e) => {
                  setLighterInput(e.target.value);
                  setSelectedCellarLighterId(null);
                  setLighterManual(e.target.value);
                  setShowLighterMatches(true);
                }}
                placeholder="Type to search your cellar or enter manually..."
                style={{ width: '100%' }}
                onFocus={() => setShowLighterMatches(true)}
                onBlur={() => setTimeout(() => setShowLighterMatches(false), 150)}
              />
              {showLighterMatches && lighterInput.trim().length > 0 && (
                <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 1000, backgroundColor: '#1a1a1a', border: '1px solid var(--color-border)', borderRadius: '6px', maxHeight: '220px', overflowY: 'auto' }}>
                  {cellarLighters
                    .filter(l => `${l.brand} ${l.style || ''}`.toLowerCase().includes(lighterInput.toLowerCase()))
                    .slice(0, 12)
                    .map(l => (
                      <button key={l.id} type="button" onMouseDown={() => {
                        setSelectedCellarLighterId(l.id);
                        setLighterManual('');
                        setLighterInput(`${l.brand}${l.style ? ` - ${l.style}` : ''} (${l.type === 'lighter' ? 'Lighter' : 'Matches'})`);
                        setShowLighterMatches(false);
                      }} style={{ width: '100%', textAlign: 'left', padding: '0.5rem', background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}>
                        {l.brand} {l.style && `- ${l.style}`} ({l.type === 'lighter' ? 'Lighter' : 'Matches'})
                      </button>
                    ))}
                </div>
              )}
            </div>
          </div>

          {/* Location Amenities */}
          <div>
            <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: '500' }}>
              Location Amenities
            </label>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={outdoorSeating}
                  onChange={(e) => setOutdoorSeating(e.target.checked)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span>Outdoor Seating</span>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={coveredSeating}
                  onChange={(e) => setCoveredSeating(e.target.checked)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span>Covered Seating</span>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={heaters}
                  onChange={(e) => setHeaters(e.target.checked)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span>Heaters</span>
              </label>
            </div>
          </div>

          {/* Public/Private Toggle */}
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Visibility
            </label>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '1rem',
              backgroundColor: isPublic ? 'rgba(196, 255, 0, 0.1)' : 'var(--color-card)',
              border: `2px solid ${isPublic ? 'var(--color-accent)' : 'var(--color-border)'}`,
              borderRadius: '8px'
            }}>
              <div>
                <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                  {isPublic ? 'Share Location' : 'Location Private'}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#999' }}>
                  {isPublic 
                    ? 'Show this check-in on the map with location' 
                    : 'Check-in visible, but location hidden from map'
                  }
                </div>
              </div>
              <button
                type="button"
                aria-pressed={isPublic}
                onClick={() => setIsPublic(!isPublic)}
                style={{
                  alignSelf: 'center',
                  minWidth: '90px',
                  height: '28px',
                  padding: '0 10px',
                  backgroundColor: isPublic ? 'var(--color-accent)' : '#666',
                  color: '#111',
                  border: 'none',
                  borderRadius: '14px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease, color 0.2s ease',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.85rem',
                  fontWeight: 600
                }}
              >
                {isPublic ? 'Public' : 'Private'}
              </button>
            </div>
          </div>

          {/* Photo Upload */}
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Photo (Optional)
            </label>

            {/* Preview */}
            {photoPreview ? (
              <div className="card-photo" style={{ marginBottom: '0.75rem' }}>
                <img
                  src={photoPreview}
                  alt="Preview"
                  style={{
                    width: '100%',
                    maxHeight: '300px',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    backgroundColor: '#1f1f1f',
                  }}
                />
              </div>
            ) : compressing ? (
              <div style={{ 
                padding: '2rem', 
                textAlign: 'center',
                border: '2px dashed #333',
                borderRadius: '8px',
                marginBottom: '0.75rem'
              }}>
                <div className="spinner" style={{ margin: '0 auto 0.5rem', width: '32px', height: '32px' }}></div>
                <span style={{ color: '#999' }}>Compressing image...</span>
              </div>
            ) : null}
            
            {/* Upload Buttons */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <label
                htmlFor="photo-gallery"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  padding: '1rem',
                  border: '2px dashed #333',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  backgroundColor: 'var(--color-card)',
                  transition: 'border-color 0.2s',
                }}
              >
                <ImageIcon size={24} color="var(--color-accent)" />
                <span style={{ fontSize: '0.875rem', textAlign: 'center' }}>Choose from Gallery</span>
              </label>

              <label
                htmlFor="photo-camera"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  padding: '1rem',
                  border: '2px dashed #333',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  backgroundColor: 'var(--color-card)',
                  transition: 'border-color 0.2s',
                }}
              >
                <ImageIcon size={24} color="var(--color-accent)" />
                <span style={{ fontSize: '0.875rem', textAlign: 'center' }}>Take Photo</span>
              </label>
            </div>

            <input
              id="photo-gallery"
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              style={{ display: 'none' }}
            />

            <input
              id="photo-camera"
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handlePhotoChange}
              style={{ display: 'none' }}
            />
          </div>

          <Button
            type="submit"
            variant="primary"
            loading={loading || compressing}
            disabled={
              (!selectedCellarTobaccoId && !selectedTobacco && !tobaccoManual.trim()) || 
              rating === 0 ||
              !locationName.trim()
            }
            fullWidth
          >
            {compressing ? 'Processing image...' : 'Create Check-in'}
          </Button>
        </form>
      </div>

      <BottomNav />
    </div>
  );
}

